import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Settings, Tag, Ruler, Plus, Pencil, Trash2, LogOut, 
  Package, ArrowLeft, MapPin, Percent, RefreshCw, Image as ImageIcon, Truck
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import LanguageSwitcher from '../components/LanguageSwitcher';

function AdminSettings({ user, onLogout }) {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const navigate = useNavigate();
  const currentLang = lang || i18n.language || 'ru';

  // Categories state
  const [categories, setCategories] = useState([]);
  const [categoryDialog, setCategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ 
    name_ru: '', name_en: '', name_kz: '',
    description_ru: '', description_en: '', description_kz: '',
    sort_order: 0 
  });
  const [deleteCategory, setDeleteCategory] = useState(null);

  // Units state
  const [units, setUnits] = useState([]);
  const [unitDialog, setUnitDialog] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [unitForm, setUnitForm] = useState({ 
    name_ru: '', name_en: '', name_kz: '',
    short_name_ru: '', short_name_en: '', short_name_kz: ''
  });
  const [deleteUnit, setDeleteUnit] = useState(null);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('categories');

  // Helper to get localized name
  const getLocalizedName = (item, field = 'name') => {
    return item[`${field}_${currentLang}`] || item[`${field}_ru`] || item[field] || '';
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const results = await Promise.allSettled([
        axios.get(`${API}/settings/categories`, { headers: getAuthHeaders() }),
        axios.get(`${API}/settings/units`, { headers: getAuthHeaders() })
      ]);
      if (results[0].status === 'fulfilled') setCategories(results[0].value.data);
      if (results[1].status === 'fulfilled') setUnits(results[1].value.data);
    } catch (error) {
      toast.error(t('adminSettings.errorLoading'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Category handlers
  const openCategoryDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        name_ru: category.name_ru || '',
        name_en: category.name_en || '',
        name_kz: category.name_kz || '',
        description_ru: category.description_ru || '',
        description_en: category.description_en || '',
        description_kz: category.description_kz || '',
        sort_order: category.sort_order || 0
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({ 
        name_ru: '', name_en: '', name_kz: '',
        description_ru: '', description_en: '', description_kz: '',
        sort_order: 0 
      });
    }
    setCategoryDialog(true);
  };

  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        const response = await axios.put(
          `${API}/settings/categories/${editingCategory.id}`,
          categoryForm,
          { headers: getAuthHeaders() }
        );
        setCategories(categories.map(c => c.id === editingCategory.id ? response.data : c));
        toast.success(t('adminSettings.categoryUpdated'));
      } else {
        const response = await axios.post(
          `${API}/settings/categories`,
          categoryForm,
          { headers: getAuthHeaders() }
        );
        setCategories([...categories, response.data]);
        toast.success(t('adminSettings.categoryCreated'));
      }
      setCategoryDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('adminSettings.errorSaving'));
    }
  };

  const handleDeleteCategory = async () => {
    try {
      await axios.delete(`${API}/settings/categories/${deleteCategory.id}`, { headers: getAuthHeaders() });
      setCategories(categories.filter(c => c.id !== deleteCategory.id));
      toast.success(t('adminSettings.categoryDeleted'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('adminSettings.errorDeleting'));
    } finally {
      setDeleteCategory(null);
    }
  };

  // Unit handlers
  const openUnitDialog = (unit = null) => {
    if (unit) {
      setEditingUnit(unit);
      setUnitForm({
        name_ru: unit.name_ru || '',
        name_en: unit.name_en || '',
        name_kz: unit.name_kz || '',
        short_name_ru: unit.short_name_ru || '',
        short_name_en: unit.short_name_en || '',
        short_name_kz: unit.short_name_kz || ''
      });
    } else {
      setEditingUnit(null);
      setUnitForm({ 
        name_ru: '', name_en: '', name_kz: '',
        short_name_ru: '', short_name_en: '', short_name_kz: ''
      });
    }
    setUnitDialog(true);
  };

  const handleSaveUnit = async () => {
    try {
      if (editingUnit) {
        const response = await axios.put(
          `${API}/settings/units/${editingUnit.id}`,
          unitForm,
          { headers: getAuthHeaders() }
        );
        setUnits(units.map(u => u.id === editingUnit.id ? response.data : u));
        toast.success(t('adminSettings.unitUpdated'));
      } else {
        const response = await axios.post(
          `${API}/settings/units`,
          unitForm,
          { headers: getAuthHeaders() }
        );
        setUnits([...units, response.data]);
        toast.success(t('adminSettings.unitCreated'));
      }
      setUnitDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('adminSettings.errorSaving'));
    }
  };

  const handleDeleteUnit = async () => {
    try {
      await axios.delete(`${API}/settings/units/${deleteUnit.id}`, { headers: getAuthHeaders() });
      setUnits(units.filter(u => u.id !== deleteUnit.id));
      toast.success(t('adminSettings.unitDeleted'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('adminSettings.errorDeleting'));
    } finally {
      setDeleteUnit(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-[1600px] mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to={`/${currentLang}`} className="flex items-center">
                <img 
                  src="/logo.jpg" 
                  alt="Ainanan" 
                  className="h-10 object-contain"
                />
              </Link>
              <div className="hidden md:block">
                <h1 className="text-lg font-semibold text-gray-900">Управление</h1>
                <p className="text-xs text-gray-500">Справочники и настройки</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => fetchData(true)}
                disabled={refreshing}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              <LanguageSwitcher />
              <Button variant="ghost" size="sm" onClick={onLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Выход
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation - Only Back button and management tabs */}
      <div className="bg-white border-b">
        <div className="max-w-[1600px] mx-auto px-4">
          <div className="flex items-center gap-2 py-3 overflow-x-auto">
            <Button onClick={() => navigate(`/${currentLang}/admin`)} variant="outline" className="shrink-0">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Назад
            </Button>
            <div className="w-px h-6 bg-gray-300 mx-2"></div>
            <Button onClick={() => navigate(`/${currentLang}/admin/products`)} variant="outline" className="shrink-0">
              <Package className="w-4 h-4 mr-2" />
              Товары
            </Button>
            <Button 
              variant={activeTab === 'categories' ? 'default' : 'outline'} 
              className={activeTab === 'categories' ? 'bg-emerald-600 hover:bg-emerald-700 shrink-0' : 'shrink-0'}
              onClick={() => setActiveTab('categories')}
            >
              <Tag className="w-4 h-4 mr-2" />
              Категории
            </Button>
            <Button 
              variant={activeTab === 'units' ? 'default' : 'outline'} 
              className={activeTab === 'units' ? 'bg-emerald-600 hover:bg-emerald-700 shrink-0' : 'shrink-0'}
              onClick={() => setActiveTab('units')}
            >
              <Ruler className="w-4 h-4 mr-2" />
              Ед. измерения
            </Button>
            <Button onClick={() => navigate(`/${currentLang}/admin/discount-groups`)} variant="outline" className="shrink-0">
              <Percent className="w-4 h-4 mr-2" />
              Группы скидок
            </Button>
            <Button onClick={() => navigate(`/${currentLang}/admin/cities`)} variant="outline" className="shrink-0">
              <MapPin className="w-4 h-4 mr-2" />
              Города
            </Button>
            <Button onClick={() => navigate(`/${currentLang}/admin/delivery-zones`)} variant="outline" className="shrink-0 bg-emerald-50 border-emerald-200 hover:bg-emerald-100">
              <Truck className="w-4 h-4 mr-2 text-emerald-600" />
              Доставка
            </Button>
            <Button onClick={() => navigate(`/${currentLang}/admin/content`)} variant="outline" className="shrink-0">
              <ImageIcon className="w-4 h-4 mr-2" />
              Контент
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 py-6">
        {/* Categories Content */}
        {activeTab === 'categories' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Категории товаров</h2>
                <p className="text-gray-500">Управление категориями для каталога</p>
              </div>
              <Button onClick={() => openCategoryDialog()} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                Добавить категорию
              </Button>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {categories.map((category) => (
                <Card key={category.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{getLocalizedName(category)}</CardTitle>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openCategoryDialog(category)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => setDeleteCategory(category)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-500">{getLocalizedName(category, 'description') || 'Без описания'}</p>
                    <p className="text-xs text-gray-400 mt-2">Сортировка: {category.sort_order}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {categories.length === 0 && (
              <Card className="p-12">
                <div className="text-center">
                  <Tag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-gray-600 mb-2">Нет категорий</h3>
                  <p className="text-gray-500 mb-6">Добавьте первую категорию для товаров</p>
                  <Button onClick={() => openCategoryDialog()} className="bg-emerald-600 hover:bg-emerald-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Добавить категорию
                  </Button>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Units Content */}
        {activeTab === 'units' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Единицы измерения</h2>
                <p className="text-gray-500">Управление единицами измерения для товаров</p>
              </div>
              <Button onClick={() => openUnitDialog()} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                Добавить единицу
              </Button>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {units.map((unit) => (
                <Card key={unit.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{getLocalizedName(unit)}</CardTitle>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openUnitDialog(unit)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => setDeleteUnit(unit)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-500">Сокращение: <span className="font-medium">{getLocalizedName(unit, 'short_name')}</span></p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {units.length === 0 && (
              <Card className="p-12">
                <div className="text-center">
                  <Ruler className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-gray-600 mb-2">Нет единиц измерения</h3>
                  <p className="text-gray-500 mb-6">Добавьте единицы измерения для товаров</p>
                  <Button onClick={() => openUnitDialog()} className="bg-emerald-600 hover:bg-emerald-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Добавить единицу
                  </Button>
                </div>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Category Dialog */}
      <Dialog open={categoryDialog} onOpenChange={setCategoryDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Редактировать категорию' : 'Новая категория'}</DialogTitle>
            <DialogDescription>Заполните данные категории на всех языках</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Название (RU) *</Label>
              <Input value={categoryForm.name_ru} onChange={(e) => setCategoryForm({...categoryForm, name_ru: e.target.value})} placeholder="Название на русском" />
            </div>
            <div className="space-y-2">
              <Label>Название (EN)</Label>
              <Input value={categoryForm.name_en} onChange={(e) => setCategoryForm({...categoryForm, name_en: e.target.value})} placeholder="Name in English" />
            </div>
            <div className="space-y-2">
              <Label>Название (KZ)</Label>
              <Input value={categoryForm.name_kz} onChange={(e) => setCategoryForm({...categoryForm, name_kz: e.target.value})} placeholder="Қазақша атауы" />
            </div>
            <div className="space-y-2">
              <Label>Описание (RU)</Label>
              <Input value={categoryForm.description_ru} onChange={(e) => setCategoryForm({...categoryForm, description_ru: e.target.value})} placeholder="Описание" />
            </div>
            <div className="space-y-2">
              <Label>Порядок сортировки</Label>
              <Input type="number" value={categoryForm.sort_order} onChange={(e) => setCategoryForm({...categoryForm, sort_order: parseInt(e.target.value) || 0})} />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setCategoryDialog(false)}>Отмена</Button>
              <Button onClick={handleSaveCategory} className="bg-emerald-600 hover:bg-emerald-700">Сохранить</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unit Dialog */}
      <Dialog open={unitDialog} onOpenChange={setUnitDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingUnit ? 'Редактировать единицу' : 'Новая единица измерения'}</DialogTitle>
            <DialogDescription>Заполните данные на всех языках</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Название (RU) *</Label>
                <Input value={unitForm.name_ru} onChange={(e) => setUnitForm({...unitForm, name_ru: e.target.value})} placeholder="Штука" />
              </div>
              <div className="space-y-2">
                <Label>Сокращение (RU) *</Label>
                <Input value={unitForm.short_name_ru} onChange={(e) => setUnitForm({...unitForm, short_name_ru: e.target.value})} placeholder="шт" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Название (EN)</Label>
                <Input value={unitForm.name_en} onChange={(e) => setUnitForm({...unitForm, name_en: e.target.value})} placeholder="Piece" />
              </div>
              <div className="space-y-2">
                <Label>Сокращение (EN)</Label>
                <Input value={unitForm.short_name_en} onChange={(e) => setUnitForm({...unitForm, short_name_en: e.target.value})} placeholder="pcs" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Название (KZ)</Label>
                <Input value={unitForm.name_kz} onChange={(e) => setUnitForm({...unitForm, name_kz: e.target.value})} placeholder="Дана" />
              </div>
              <div className="space-y-2">
                <Label>Сокращение (KZ)</Label>
                <Input value={unitForm.short_name_kz} onChange={(e) => setUnitForm({...unitForm, short_name_kz: e.target.value})} placeholder="дн" />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setUnitDialog(false)}>Отмена</Button>
              <Button onClick={handleSaveUnit} className="bg-emerald-600 hover:bg-emerald-700">Сохранить</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Category Confirmation */}
      <AlertDialog open={!!deleteCategory} onOpenChange={() => setDeleteCategory(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить категорию?</AlertDialogTitle>
            <AlertDialogDescription>
              Вы уверены, что хотите удалить категорию "{deleteCategory && getLocalizedName(deleteCategory)}"? 
              Это действие нельзя отменить.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCategory} className="bg-red-600 hover:bg-red-700">
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Unit Confirmation */}
      <AlertDialog open={!!deleteUnit} onOpenChange={() => setDeleteUnit(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить единицу измерения?</AlertDialogTitle>
            <AlertDialogDescription>
              Вы уверены, что хотите удалить единицу "{deleteUnit && getLocalizedName(deleteUnit)}"?
              Это действие нельзя отменить.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUnit} className="bg-red-600 hover:bg-red-700">
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default AdminSettings;
